import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Download, Eye, FileText, Gear, MagnifyingGlass, Ticket, Trash, ShieldCheck, EyeSlash } from '@phosphor-icons/react';

import { DashboardLayout } from '../components/DashboardLayout';
import { API, BACKEND_URL, formatApiErrorDetail } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['all', 'new', 'in_review', 'inquiry_open', 'approved', 'rejected', 'ticket_generated', 'ticket_sent', 'archived'];

const STATUS_LABELS = {
  new: 'Neu',
  in_review: 'In Prüfung',
  inquiry_open: 'Rückfrage offen',
  approved: 'Freigegeben',
  rejected: 'Abgelehnt',
  ticket_generated: 'Ticket erzeugt',
  ticket_sent: 'Ticket versendet',
  archived: 'Archiviert',
};

const TICKET_STATUS_LABELS = {
  pending_approval: 'Wartet auf Freigabe',
  generated: 'Erzeugt',
  render_failed: 'Renderfehler',
  archived: 'Archiviert',
};

export function SubmissionsPage() {
  const [searchParams] = useSearchParams();
  const [submissions, setSubmissions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState(searchParams.get('campaign_id') || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [statusDraft, setStatusDraft] = useState('new');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (campaignFilter !== 'all') params.campaign_id = campaignFilter;
      if (statusFilter !== 'all') params.review_status = statusFilter;

      const [submissionResponse, campaignResponse] = await Promise.all([
        axios.get(`${API}/submissions`, { params, withCredentials: true }),
        axios.get(`${API}/campaigns`, { withCredentials: true }),
      ]);
      setSubmissions(submissionResponse.data || []);
      setCampaigns(campaignResponse.data || []);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Submissions konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [campaignFilter, statusFilter]);

  const filteredSubmissions = useMemo(() => submissions.filter((submission) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    const values = [
      submission.serial_number,
      submission.campaign_name,
      submission.review_status,
      ...Object.values(submission.data || {}).map(String),
    ].filter(Boolean).join(' ').toLowerCase();
    return values.includes(query);
  }), [search, submissions]);

  const handleDelete = async (id) => {
    if (!window.confirm('Soll diese Submission als gelöscht markiert werden?')) return;
    try {
      await axios.delete(`${API}/submissions/${id}`, { withCredentials: true });
      toast.success('Submission wurde als gelöscht markiert.');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Löschen fehlgeschlagen.');
    }
  };

  const handleAnonymize = async (submission) => {
    try {
      const { data } = await axios.post(`${API}/submissions/${submission.id}/anonymize`, {}, { withCredentials: true });
      toast.success('Personenbezogene Daten wurden anonymisiert.');
      if (selectedSubmission?.id === submission.id) {
        setSelectedSubmission(data);
      }
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Anonymisierung fehlgeschlagen.');
    }
  };

  const handleGenerateTicket = async (submission) => {
    try {
      await axios.post(`${API}/tickets/from-submission/${submission.id}`, {}, { withCredentials: true });
      const { data } = await axios.get(`${API}/submissions/${submission.id}`, { withCredentials: true });
      if (selectedSubmission?.id === submission.id) {
        setSelectedSubmission(data);
        setStatusDraft(data.review_status || 'new');
      }
      toast.success('Ticket erzeugt und gespeichert.');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Ticket-Erzeugung fehlgeschlagen.');
    }
  };

  const saveStatus = async () => {
    if (!selectedSubmission) return;
    setUpdatingStatus(true);
    try {
      const { data } = await axios.put(`${API}/submissions/${selectedSubmission.id}`, { review_status: statusDraft }, { withCredentials: true });
      toast.success('Workflow-Status aktualisiert.');
      setSelectedSubmission(data);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Status konnte nicht aktualisiert werden.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openSubmissionDetail = (submission) => {
    setSelectedSubmission(submission);
    setStatusDraft(submission.review_status || 'new');
  };

  const badgeClass = (status) => ({
    new: 'bg-sky-100 text-sky-700',
    in_review: 'bg-amber-100 text-amber-700',
    inquiry_open: 'bg-violet-100 text-violet-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    ticket_generated: 'bg-blue-100 text-blue-700',
    ticket_sent: 'bg-emerald-100 text-emerald-700',
    archived: 'bg-zinc-100 text-zinc-700',
  }[status] || 'bg-zinc-100 text-zinc-700');

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="submissions-page">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-zinc-900">Submissions</h1>
            <p className="text-zinc-500 mt-1">Einreichungen mit Ticketbezug, Freigabe und Ticket-Erzeugung</p>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px_220px]">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Submission durchsuchen..." className="pl-9" data-testid="submission-search" />
          </div>
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger data-testid="campaign-filter"><SelectValue placeholder="Kampagne" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kampagnen</SelectItem>
              {campaigns.map((campaign) => <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((status) => <SelectItem key={status} value={status}>{status === 'all' ? 'Alle Status' : STATUS_LABELS[status]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-3" data-testid="submissions-loading-state">
            {[1, 2, 3, 4].map((item) => <div key={item} className="h-16 bg-zinc-100 animate-pulse rounded-sm" />)}
          </div>
        ) : filteredSubmissions.length > 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="submissions-table">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px]">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                    <th className="px-4 py-3">Seriennummer</th>
                    <th className="px-4 py-3">Kampagne</th>
                    <th className="px-4 py-3">Daten</th>
                    <th className="px-4 py-3">Submission</th>
                    <th className="px-4 py-3">Ticket</th>
                    <th className="px-4 py-3">Datum</th>
                    <th className="px-4 py-3 text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map((submission) => (
                    <tr key={submission.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`submission-row-${submission.serial_number}`}>
                      <td className="px-4 py-4 font-mono text-sm text-zinc-950">{submission.serial_number}</td>
                      <td className="px-4 py-4 text-sm text-zinc-700">{submission.campaign_name}</td>
                      <td className="px-4 py-4 text-sm text-zinc-600 max-w-xs truncate">
                        {Object.entries(submission.data || {}).slice(0, 2).map(([key, value]) => `${key}: ${String(value).slice(0, 24)}`).join(' · ')}
                      </td>
                      <td className="px-4 py-4"><span className={`${badgeClass(submission.review_status)} px-2 py-1 rounded-sm text-xs font-medium`}>{STATUS_LABELS[submission.review_status] || submission.review_status}</span></td>
                      <td className="px-4 py-4">
                        {submission.ticket_status ? (
                          <span className={`${badgeClass(submission.ticket_status)} px-2 py-1 rounded-sm text-xs font-medium`}>{TICKET_STATUS_LABELS[submission.ticket_status] || submission.ticket_status}</span>
                        ) : (
                        <span className="text-xs text-zinc-500">Kein Ticket</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-zinc-500">{new Date(submission.created_at).toLocaleString('de-DE')}</td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => openSubmissionDetail(submission)} data-testid={`submission-open-button-${submission.id}`}>
                            <Eye className="w-4 h-4" />
                            Details
                          </Button>
                          {submission.ticket_instance_id ? (
                            <Button variant="outline" asChild data-testid={`submission-open-ticket-${submission.id}`}>
                              <a href={`${BACKEND_URL}/api/tickets/${submission.ticket_instance_id}/file`} target="_blank" rel="noopener noreferrer">
                                <Download className="w-4 h-4" />
                                Ticket
                              </a>
                            </Button>
                          ) : (
                            <Button variant="outline" onClick={() => handleGenerateTicket(submission)} data-testid={`submission-generate-ticket-${submission.id}`}>
                              <Ticket className="w-4 h-4" />
                              Ticket erzeugen
                            </Button>
                          )}
                          <Button variant="outline" onClick={() => handleDelete(submission.id)} data-testid={`submission-delete-${submission.id}`}>
                            <Trash className="w-4 h-4 text-[#E63946]" />
                            Soft Delete
                          </Button>
                          <Button variant="outline" onClick={() => handleAnonymize(submission)} data-testid={`submission-anonymize-${submission.id}`}>
                            <EyeSlash className="w-4 h-4" />
                            Anonymisieren
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-white border border-zinc-200 rounded-sm" data-testid="submissions-empty-state">
            <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">Keine Submissions gefunden.</p>
          </div>
        )}
      </div>

      <Dialog open={Boolean(selectedSubmission)} onOpenChange={(open) => !open && setSelectedSubmission(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-sm border-2 border-zinc-950 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]" data-testid="submission-detail-dialog">
          <DialogHeader>
            <DialogTitle data-testid="submission-detail-dialog-title">Submission Detail</DialogTitle>
            <DialogDescription data-testid="submission-detail-dialog-description">Daten, Workflow-Status und Ticket-Verknüpfung im Überblick.</DialogDescription>
          </DialogHeader>
          {selectedSubmission && (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] py-2">
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="p-3 bg-zinc-50 rounded-sm"><span className="text-sm text-zinc-500">Seriennummer</span><p className="font-mono font-medium text-zinc-950">{selectedSubmission.serial_number}</p></div>
                  <div className="p-3 bg-zinc-50 rounded-sm"><span className="text-sm text-zinc-500">Kampagne</span><p className="font-medium text-zinc-950">{selectedSubmission.campaign_name}</p></div>
                  <div className="p-3 bg-zinc-50 rounded-sm"><span className="text-sm text-zinc-500">Submission-Status</span><p className="font-medium text-zinc-950">{STATUS_LABELS[selectedSubmission.review_status] || selectedSubmission.review_status}</p></div>
                  <div className="p-3 bg-zinc-50 rounded-sm"><span className="text-sm text-zinc-500">Ticket-Status</span><p className="font-medium text-zinc-950">{TICKET_STATUS_LABELS[selectedSubmission.ticket_status] || selectedSubmission.ticket_status || '—'}</p></div>
                  <div className="p-3 bg-zinc-50 rounded-sm"><span className="text-sm text-zinc-500">Datenschutz</span><p className="font-medium text-zinc-950">{selectedSubmission.consent_given ? 'Zustimmung gespeichert' : 'Keine Zustimmung'}</p></div>
                  <div className="p-3 bg-zinc-50 rounded-sm"><span className="text-sm text-zinc-500">Datenstatus</span><p className="font-medium text-zinc-950">{selectedSubmission.deleted ? 'Gelöscht markiert' : selectedSubmission.anonymized ? 'Anonymisiert' : 'Aktiv'}</p></div>
                </div>

                <div className="border-t border-zinc-200 pt-4">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-3">Formulardaten</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedSubmission.data || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between gap-4 p-3 bg-zinc-50 rounded-sm">
                        <span className="text-sm text-zinc-500 capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-right max-w-[60%] break-words">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Workflow</h4>
                  <Select value={statusDraft} onValueChange={setStatusDraft}>
                    <SelectTrigger data-testid="submission-status-update-select"><SelectValue placeholder="Status setzen" /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.filter((status) => status !== 'all').map((status) => <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={saveStatus} disabled={updatingStatus} className="w-full bg-[#002FA7] text-white hover:bg-[#002FA7]/90" data-testid="submission-status-save-button">
                    <Gear className="w-4 h-4" />
                    {updatingStatus ? 'Speichert...' : 'Status speichern'}
                  </Button>
                </div>

                <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Ticket</h4>
                  {selectedSubmission.ticket_instance_id ? (
                    <>
                      <Button variant="outline" asChild className="w-full" data-testid="submission-dialog-ticket-open-button">
                        <a href={`${BACKEND_URL}/api/tickets/${selectedSubmission.ticket_instance_id}/file`} target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4" />
                          Persistiertes Ticket öffnen
                        </a>
                      </Button>
                      {selectedSubmission.ticket_public_url && (
                        <Button variant="outline" asChild className="w-full" data-testid="submission-dialog-public-ticket-button">
                          <a href={selectedSubmission.ticket_public_url} target="_blank" rel="noopener noreferrer">
                            <Eye className="w-4 h-4" />
                            Öffentlichen Ticketlink öffnen
                          </a>
                        </Button>
                      )}
                    </>
                  ) : (
                    <Button variant="outline" onClick={() => handleGenerateTicket(selectedSubmission)} className="w-full" data-testid="submission-dialog-generate-ticket-button">
                      <Ticket className="w-4 h-4" />
                      Ticket jetzt erzeugen
                    </Button>
                  )}
                </div>
                <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Datenschutz</h4>
                  <div className="space-y-2 text-sm text-zinc-700">
                    <p><span className="text-zinc-500">Consent-Zeitpunkt:</span> <span className="font-medium text-zinc-950">{selectedSubmission.consent_timestamp ? new Date(selectedSubmission.consent_timestamp).toLocaleString('de-DE') : '—'}</span></p>
                    <p><span className="text-zinc-500">Consent-Version:</span> <span className="font-medium text-zinc-950">{selectedSubmission.consent_version || '—'}</span></p>
                    <p><span className="text-zinc-500">IP:</span> <span className="font-medium text-zinc-950">{selectedSubmission.ip_address || '—'}</span></p>
                  </div>
                  <div className="space-y-2 rounded-sm border border-zinc-200 bg-zinc-50 p-3" data-testid="submission-consent-records-panel">
                    {Object.entries(selectedSubmission.consent_records || {}).length > 0 ? Object.entries(selectedSubmission.consent_records || {}).map(([key, value]) => (
                      <div key={key} className="text-sm text-zinc-700">
                        <p className="font-medium text-zinc-950 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> {key}</p>
                        <p>Akzeptiert: {value.accepted ? 'Ja' : 'Nein'}</p>
                        <p>Snapshot: {value.text_snapshot || '—'}</p>
                      </div>
                    )) : <p className="text-sm text-zinc-500">Keine Consent-Details gespeichert.</p>}
                  </div>
                  <div className="grid gap-2">
                    <Button variant="outline" onClick={() => handleAnonymize(selectedSubmission)} data-testid="submission-dialog-anonymize-button">
                      <EyeSlash className="w-4 h-4" />
                      Daten anonymisieren
                    </Button>
                    <Button variant="outline" onClick={() => handleDelete(selectedSubmission.id)} data-testid="submission-dialog-softdelete-button">
                      <Trash className="w-4 h-4 text-[#E63946]" />
                      Daten löschen (Soft Delete)
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
